/**
 * GardenTrails — Phase 6 Collection Page JavaScript
 * File: assets/gardentrails-phase6.js
 * BRD: BRD_COLLECTION-PAGE_PHASE-6_24APR2026_v2.md — Sessions 2, 3, 4
 * Loaded via: <script src="..." defer> in sections/gt-collection.liquid
 * Author: Claude (GardenTrails Project) · 24 April 2026
 *
 * Session 2 modules:
 *   1. initSortDropdown      — lightweight popover for the sort button
 *   2. initLoadMore          — fetch + append new cards via ?section_id=
 *   3. initTouchSuppression  — disables hover swap + quick-add slide-up on touch devices
 *   4. initStickyToolbar     — toggles `is-scrolled` class when toolbar pins
 *   5. liveAnnounce (util)   — writes to the aria-live region
 *
 * Session 3 modules (THIS SESSION):
 *   6. initFilterPanel       — right-overlay dialog with backdrop
 *      · focus trap (start/end sentinels)
 *      · body scroll lock via .gt-body--filter-open class
 *      · Escape to close, backdrop click to close, Apply click to close
 *      · accordion group toggles (aria-expanded)
 *      · show-more overflow for filters with >10 values
 *      · instant-apply for list/boolean (via filter_value.url_to_add)
 *      · price range dual slider + numeric inputs + Apply button
 *      · above-grid chip removes also wired
 *
 * Session 4 will add: initQuickAddDrawer
 *
 * Event contract:
 *   — Dispatches `gt:col:loaded-more` on the section wrapper after Load More
 *   — Reads Heritage's cart:update pattern for Quick Add (future session)
 */
(function () {
  'use strict';
 
  /* ═══════════════════════════════════════════════════════════
     UTILITIES
  ═══════════════════════════════════════════════════════════ */

  /**
   * Writes a message to the section's aria-live region.
   * Uses :empty CSS + setTimeout re-announce to reliably re-trigger SR.
   * @param {Element} sectionEl
   * @param {string} message
   */
  function liveAnnounce(sectionEl, message) {
    const live = sectionEl.querySelector('[data-gt-col-live]');
    if (!live) return;
    // Clear first, then set — ensures even identical messages re-announce
    live.textContent = '';
    // Next tick so screen readers notice the change
    setTimeout(function () { live.textContent = message; }, 50);
  }

  /**
   * Simple i18n helper — reads translated strings from a JSON blob
   * embedded on the section wrapper.
   * @param {Element} sectionEl
   * @param {string} key
   * @param {Object} [vars]
   * @returns {string}
   */
  function t(sectionEl, key, vars) {
    try {
      var raw = sectionEl.getAttribute('data-gt-i18n');
      if (!raw) return key;
      var dict = JSON.parse(raw);
      var str = dict[key] || key;
      if (vars) {
        Object.keys(vars).forEach(function (k) {
          str = str.replace('{{ ' + k + ' }}', vars[k]);
        });
      }
      return str;
    } catch (e) {
      return key;
    }
  }

  /**
   * Detects touch-only devices. Hover events do fire on touch devices
   * (mobile Safari fires :hover on first tap), so we key off pointer types.
   * @returns {boolean}
   */
  function isTouchDevice() {
    return window.matchMedia('(hover: none)').matches;
  }

  /**
   * Escape CSS attribute selector values.
   * @param {string} v
   * @returns {string}
   */
  function cssEscape(v) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(v);
    return String(v).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  /* ═══════════════════════════════════════════════════════════
     MODULE 1 — SORT DROPDOWN
     Lightweight custom popover. Replaces native <select>.
     Reads sort options from the hidden <select> that serves as the
     JS-disabled fallback. Toggles open/close, updates visible label,
     submits sort change via URL navigation.
  ═══════════════════════════════════════════════════════════ */

  function initSortDropdown(sectionEl) {
    var wrapper = sectionEl.querySelector('[data-gt-sort-wrap]');
    if (!wrapper) return;

    var trigger = wrapper.querySelector('[data-gt-sort-trigger]');
    var popover = wrapper.querySelector('[data-gt-sort-popover]');
    var options = wrapper.querySelectorAll('[data-gt-sort-option]');
    var label   = wrapper.querySelector('[data-gt-sort-label]');
    if (!trigger || !popover || !options.length) return;

    var isOpen = false;

    function open() {
      popover.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      isOpen = true;
      // Focus selected option on open for keyboard users
      var selected = popover.querySelector('[data-gt-sort-option][aria-selected="true"]');
      (selected || options[0]).focus();
      document.addEventListener('click', onOutsideClick);
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      popover.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      isOpen = false;
      document.removeEventListener('click', onOutsideClick);
      document.removeEventListener('keydown', onKeydown);
    }

    function onOutsideClick(e) {
      if (!wrapper.contains(e.target)) close();
    }

    function onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        trigger.focus();
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        var list = Array.prototype.slice.call(options);
        var idx = list.indexOf(document.activeElement);
        var next = e.key === 'ArrowDown'
          ? (idx + 1) % list.length
          : (idx - 1 + list.length) % list.length;
        list[next].focus();
      } else if (e.key === 'Home') {
        e.preventDefault();
        options[0].focus();
      } else if (e.key === 'End') {
        e.preventDefault();
        options[options.length - 1].focus();
      }
    }

    trigger.addEventListener('click', function (e) {
      e.stopPropagation();
      isOpen ? close() : open();
    });

    options.forEach(function (opt) {
      opt.addEventListener('click', function () {
        selectOption(opt);
      });
      opt.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          selectOption(opt);
        }
      });
    });

    function selectOption(opt) {
      var value = opt.getAttribute('data-gt-sort-option');
      var text = opt.textContent.trim();

      // Update visual state
      options.forEach(function (o) { o.setAttribute('aria-selected', 'false'); });
      opt.setAttribute('aria-selected', 'true');
      if (label) label.textContent = text;

      close();
      trigger.focus();

      // Navigate to the sort URL
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', value);
      url.searchParams.delete('page'); // reset to page 1 on sort change

      // Attempt AJAX update via section renderer; fall back to full-page nav
      if (window.GTCollection && typeof window.GTCollection.fetchAndSwap === 'function') {
        window.GTCollection.fetchAndSwap(sectionEl, url, {
          live: t(sectionEl, 'a11y_sort_changed', { sort_name: text })
        });
      } else {
        window.location.href = url.toString();
      }
    }

    // Roving tabindex — only trigger is Tab-reachable by default
    options.forEach(function (opt, i) {
      opt.setAttribute('tabindex', i === 0 ? '0' : '-1');
    });
  }

  /* ═══════════════════════════════════════════════════════════
     MODULE 2 — LOAD MORE (AJAX)
     Fetches next page via ?section_id=... returns HTML fragment,
     extracts new cards, appends to existing grid.
     Updates URL via history.pushState. Handles reduced-motion +
     iOS scroll anchoring (overflow-anchor: none set in CSS).
  ═══════════════════════════════════════════════════════════ */

  /** In-memory cache for fetched section HTML, keyed by URL */
  var sectionCache = new Map();

  /**
   * Fetches a section HTML fragment using Shopify's ?section_id= endpoint.
   * Heritage uses the same pattern (assets/section-renderer.js line 122).
   * @param {string} sectionId
   * @param {URL} url
   * @returns {Promise<string>}
   */
  function fetchSectionHTML(sectionId, url) {
    // Normalize: Shopify section IDs are passed without the "shopify-section-" prefix
    var sid = sectionId.replace(/^shopify-section-/, '');
    var fetchUrl = new URL(url.toString());
    fetchUrl.searchParams.set('section_id', sid);

    var cacheKey = fetchUrl.toString();
    if (sectionCache.has(cacheKey)) return Promise.resolve(sectionCache.get(cacheKey));

    return fetch(fetchUrl.toString(), { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('Section fetch failed: ' + res.status);
        return res.text();
      })
      .then(function (html) {
        sectionCache.set(cacheKey, html);
        return html;
      });
  }

  /**
   * Parses a section HTML fragment (inert via DOMParser) and returns the
   * first .gt-col element inside.
   * @param {string} html
   * @returns {Element|null}
   */
  function parseFragment(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector('.gt-col') || doc.querySelector('[data-gt-col]') || doc.firstElementChild;
  }

  /**
   * Initializes the Load More button on a section.
   */
  function initLoadMore(sectionEl) {
    var button = sectionEl.querySelector('[data-gt-load-more]');
    if (!button) return;

    button.addEventListener('click', function (e) {
      // Progressive enhancement — let the link work without JS
      if (e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      loadMorePage(sectionEl, button);
    });
  }

  function loadMorePage(sectionEl, button) {
    var nextUrl = button.getAttribute('data-next-url');
    var sectionId = button.getAttribute('data-section-id');
    if (!nextUrl || !sectionId) return;

    var origLabel = button.querySelector('.gt-col__load-more-label');
    var origText = origLabel ? origLabel.textContent : '';

    button.setAttribute('aria-busy', 'true');
    if (origLabel) origLabel.textContent = t(sectionEl, 'loading');

    fetchSectionHTML(sectionId, new URL(nextUrl, window.location.origin))
      .then(function (html) {
        var fragment = parseFragment(html);
        if (!fragment) throw new Error('Fragment parse returned null');

        // Extract the new grid items from the fragment
        var newItems = fragment.querySelectorAll('.gt-col__grid-item');
        var existingGrid = sectionEl.querySelector('[data-gt-grid]');
        if (!newItems.length || !existingGrid) {
          // Nothing to append — fall through to full nav as safety net
          window.location.href = nextUrl;
          return;
        }

        var firstNewItemRef = null;
        newItems.forEach(function (item, idx) {
          var cloned = item.cloneNode(true);
          existingGrid.appendChild(cloned);
          if (idx === 0) firstNewItemRef = cloned;
        });

        // Find the next page URL from the fetched response
        var newLoadMore = fragment.querySelector('[data-gt-load-more]');
        var loadMoreWrap = sectionEl.querySelector('.gt-col__load-more-wrap');
        if (newLoadMore && newLoadMore.getAttribute('data-next-url')) {
          button.setAttribute('data-next-url', newLoadMore.getAttribute('data-next-url'));
          button.setAttribute('href', newLoadMore.getAttribute('data-next-url'));
        } else {
          // No more pages — remove the Load More wrapper
          if (loadMoreWrap && loadMoreWrap.parentNode) {
            loadMoreWrap.parentNode.removeChild(loadMoreWrap);
          }
        }

        // Update counter
        var showingEl = sectionEl.querySelector('[data-gt-showing]');
        var newShowing = fragment.querySelector('[data-gt-showing]');
        if (showingEl && newShowing) {
          showingEl.innerHTML = newShowing.innerHTML;
          showingEl.setAttribute('data-shown', newShowing.getAttribute('data-shown') || '');
        }

        // Update browser URL (shareable filtered + paginated state)
        try {
          var currentLoaded = (history.state && history.state.gtLoadedPages) || 1;
          history.pushState(
            { gtLoadedPages: currentLoaded + 1, gtBaseUrl: window.location.href },
            '',
            nextUrl
          );
        } catch (err) {
          // pushState can throw in some contexts (e.g. file:// preview) — non-fatal
        }

        // Accessibility announcement
        var shown = showingEl ? (showingEl.getAttribute('data-shown') || '?') : '?';
        var total = showingEl ? (showingEl.getAttribute('data-total') || '?') : '?';
        liveAnnounce(sectionEl, t(sectionEl, 'a11y_more_loaded', {
          count: newItems.length, shown: shown, total: total
        }));

        // Dispatch custom event so Session 4's Quick Add can re-scan if needed
        sectionEl.dispatchEvent(new CustomEvent('gt:col:loaded-more', {
          bubbles: true,
          detail: { count: newItems.length, shown: shown, total: total }
        }));

        // Scroll smoothly to first new card (respect reduced motion)
        if (firstNewItemRef && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
          // Small delay so layout settles before scrollIntoView
          setTimeout(function () {
            firstNewItemRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 50);
        }
      })
      .catch(function (err) {
        // Graceful degradation — fall through to regular navigation
        // Console error is OK here: Session 4 QC will add an error toast
        if (window.console && console.error) {
          console.error('[gt-collection] Load More failed, falling back to navigation:', err);
        }
        window.location.href = nextUrl;
      })
      .then(function () {
        button.setAttribute('aria-busy', 'false');
        if (origLabel) origLabel.textContent = origText;
      });
  }

  /* ═══════════════════════════════════════════════════════════
     MODULE 3 — TOUCH SUPPRESSION
     CSS uses :hover for image swap + Quick Add slide-up. On touch
     devices, iOS Safari fires :hover on tap, causing the first tap
     to reveal Quick Add instead of opening the product. We tag the
     section with `data-gt-touch` when pointer:coarse, and CSS reads
     this to disable the hover effects on those devices.
  ═══════════════════════════════════════════════════════════ */

  function initTouchSuppression(sectionEl) {
    if (isTouchDevice()) {
      sectionEl.setAttribute('data-gt-touch', 'true');
    }
    // Listen for pointer-type changes (rare — e.g. connecting bluetooth mouse on iPad)
    try {
      var mq = window.matchMedia('(hover: none)');
      var listener = function (e) {
        if (e.matches) sectionEl.setAttribute('data-gt-touch', 'true');
        else sectionEl.removeAttribute('data-gt-touch');
      };
      if (mq.addEventListener) mq.addEventListener('change', listener);
      else if (mq.addListener) mq.addListener(listener); // Safari <14
    } catch (e) { /* non-fatal */ }
  }

  /* ═══════════════════════════════════════════════════════════
     MODULE 4 — STICKY TOOLBAR ENHANCEMENT
     When the sticky toolbar reaches top:0, add an `is-scrolled` class
     so CSS can add a bottom-border / subtle shadow for separation.
     Uses IntersectionObserver with a sentinel element above the toolbar.
  ═══════════════════════════════════════════════════════════ */

  function initStickyToolbar(sectionEl) {
    var toolbar = sectionEl.querySelector('[data-gt-toolbar]');
    if (!toolbar || !toolbar.classList.contains('gt-col__toolbar--sticky')) return;

    // Create sentinel element right before the toolbar
    var sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'position:absolute;height:1px;width:1px;visibility:hidden;';
    toolbar.parentNode.insertBefore(sentinel, toolbar);

    if (!('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      var entry = entries[0];
      if (entry.isIntersecting) {
        toolbar.classList.remove('is-scrolled');
      } else {
        toolbar.classList.add('is-scrolled');
      }
    }, { threshold: [0] });

    observer.observe(sentinel);
  }

  /* ═══════════════════════════════════════════════════════════
     PUBLIC API
     Exposes selected internals for other GT modules (Quick Add in
     Session 4 will call `fetchAndSwap` when filters apply).
  ═══════════════════════════════════════════════════════════ */

  window.GTCollection = {
    fetchAndSwap: function (sectionEl, url, opts) {
      opts = opts || {};
      var sectionId = sectionEl.getAttribute('data-section-id');
      if (!sectionId) return;

      var grid = sectionEl.querySelector('[data-gt-grid]');
      if (grid) grid.classList.add('is-loading');

      fetchSectionHTML(sectionId, url)
        .then(function (html) {
          var fragment = parseFragment(html);
          if (!fragment) throw new Error('Fragment parse returned null');

          // Replace the grid + toolbar count + chips + filter-panel inner content.
          // NOTE: we swap the panel's INNER regions (chips + scroll body), not the
          // panel wrapper itself — preserves open/closed state, scroll position,
          // and in-flight interactions (price slider drag). Without this, the panel
          // would show stale unchecked boxes after a user activates a filter.
          ['[data-gt-grid]', '[data-gt-showing]', '.gt-col__count-text', '.gt-col__chips', '.gt-col__load-more-wrap', '[data-gt-fp-chips]', '[data-gt-fp-scroll]'].forEach(function (sel) {
            var oldEl = sectionEl.querySelector(sel);
            var newEl = fragment.querySelector(sel);
            if (oldEl && newEl) {
              oldEl.innerHTML = newEl.innerHTML;
              // Copy attributes that drive state (data-shown, data-total, etc.)
              Array.prototype.slice.call(newEl.attributes).forEach(function (attr) {
                if (attr.name.indexOf('data-') === 0) oldEl.setAttribute(attr.name, attr.value);
              });
            } else if (oldEl && !newEl) {
              // Element was removed in new state (e.g. chips gone after Clear all)
              oldEl.innerHTML = '';
            }
          });

          // Re-wire the filter panel's interactive elements after innerHTML swap
          // (innerHTML replacement destroys event listeners on children).
          var panelToRewire = sectionEl.querySelector('[data-gt-filter-panel]') ||
            (function () {
              var btn = sectionEl.querySelector('[data-gt-filter-open]');
              var pid = btn && btn.getAttribute('aria-controls');
              return pid ? document.getElementById(pid) : null;
            })();
          if (panelToRewire && typeof window.GTCollection._wirePanelControls === 'function') {
            window.GTCollection._wirePanelControls(panelToRewire, sectionEl);
          }

          // Also update the toolbar filter-button count badge to match new URL state
          var oldBadge = sectionEl.querySelector('.gt-col__filter-btn-count');
          var newBadge = fragment.querySelector('.gt-col__filter-btn-count');
          var filterBtn = sectionEl.querySelector('[data-gt-filter-open]');
          if (newBadge && oldBadge) {
            oldBadge.textContent = newBadge.textContent;
          } else if (newBadge && filterBtn) {
            filterBtn.appendChild(newBadge.cloneNode(true));
          } else if (oldBadge && !newBadge) {
            oldBadge.parentNode.removeChild(oldBadge);
          }

          try {
            history.pushState({ gtFilterState: url.search }, '', url.toString());
          } catch (err) { /* non-fatal */ }

          if (opts.live) liveAnnounce(sectionEl, opts.live);
        })
        .catch(function (err) {
          if (window.console && console.error) console.error('[gt-collection] fetchAndSwap failed:', err);
          window.location.href = url.toString();
        })
        .then(function () {
          if (grid) grid.classList.remove('is-loading');
        });
    },

    liveAnnounce: liveAnnounce,

    fetchSectionHTML: fetchSectionHTML,

    // Internal — called by fetchAndSwap after panel innerHTML replacement
    // to re-attach event listeners. Public due to IIFE scoping, but
    // underscore-prefixed to signal "don't call from outside modules".
    _wirePanelControls: function (panel, sectionEl) {
      wirePanelControls(panel, sectionEl);
    }
  };

  /* ═══════════════════════════════════════════════════════════
     MODULE 6 — FILTER PANEL (Session 3)
     Right-overlay dialog with backdrop. Focus trap + scroll lock.
     Handles: open/close, accordion toggles, show-more overflow,
     instant-apply for list/boolean filters via filter_value.url_to_add,
     price range dual slider + numeric inputs + Apply button.
     Fetch-and-swap via window.GTCollection.fetchAndSwap for all changes.
  ═══════════════════════════════════════════════════════════ */

  function initFilterPanel(sectionEl) {
    // Panel is usually a descendant of the section wrapper, but can be pulled
    // outside by theme-editor reorderings or future refactors. Fall back to
    // document-wide lookup by aria-controls → id mapping before giving up.
    var panel = sectionEl.querySelector('[data-gt-filter-panel]');
    if (!panel) {
      var trigger0 = sectionEl.querySelector('[data-gt-filter-open]');
      var panelId = trigger0 && trigger0.getAttribute('aria-controls');
      if (panelId) panel = document.getElementById(panelId);
    }
    if (!panel) return; // No filters defined in Shopify Admin → nothing to init

    var trigger = sectionEl.querySelector('[data-gt-filter-open]');
    var backdrop = sectionEl.querySelector('[data-gt-filter-backdrop]') ||
                   document.querySelector('[data-gt-filter-backdrop]');
    var closeBtn = panel.querySelector('[data-gt-filter-close]');
    var applyBtn = panel.querySelector('[data-gt-filter-apply]');
    if (!trigger) return;

    var lastFocusedBeforeOpen = null;
    var isOpen = false;

    /* ── Open / close ─────────────────────────────── */

    function open() {
      if (isOpen) return;
      isOpen = true;
      lastFocusedBeforeOpen = document.activeElement;
      panel.hidden = false;
      if (backdrop) { backdrop.hidden = false; backdrop.setAttribute('aria-hidden', 'false'); }
      trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('gt-body--filter-open');
      // Defer focus until panel transitions in, so screen readers don't skip
      requestAnimationFrame(function () {
        panel.classList.add('gt-filter-panel--open');
        if (backdrop) backdrop.classList.add('gt-filter-backdrop--open');
        // Focus the close button for keyboard users
        if (closeBtn) closeBtn.focus();
      });
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      panel.classList.remove('gt-filter-panel--open');
      if (backdrop) backdrop.classList.remove('gt-filter-backdrop--open');
      trigger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('gt-body--filter-open');
      document.removeEventListener('keydown', onKeydown);
      // Hide after transition so aria-hidden cascades properly
      setTimeout(function () {
        if (!isOpen) {
          panel.hidden = true;
          if (backdrop) { backdrop.hidden = true; backdrop.setAttribute('aria-hidden', 'true'); }
        }
      }, 300);
      // Return focus to the trigger
      if (lastFocusedBeforeOpen && typeof lastFocusedBeforeOpen.focus === 'function') {
        lastFocusedBeforeOpen.focus();
      } else {
        trigger.focus();
      }
    }

    function onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    }

    trigger.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (backdrop) backdrop.addEventListener('click', close);
    if (applyBtn) applyBtn.addEventListener('click', close);

    /* ── Focus trap ───────────────────────────────── */

    var trapStart = panel.querySelector('[data-gt-focus-trap="start"]');
    var trapEnd = panel.querySelector('[data-gt-focus-trap="end"]');

    function focusableElements() {
      var selector = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
      var all = Array.prototype.slice.call(panel.querySelectorAll(selector));
      return all.filter(function (el) {
        return !el.hasAttribute('data-gt-focus-trap') &&
               !el.hasAttribute('aria-hidden') &&
               el.offsetParent !== null; // visible
      });
    }

    if (trapStart) {
      trapStart.addEventListener('focus', function () {
        var els = focusableElements();
        if (els.length) els[els.length - 1].focus();
      });
    }
    if (trapEnd) {
      trapEnd.addEventListener('focus', function () {
        var els = focusableElements();
        if (els.length) els[0].focus();
      });
    }

    /* ── Wire interactive controls inside the panel (accordion, show-more,
          filter links, chip removes, price slider). Extracted so it can be
          called again after fetchAndSwap replaces the panel's innerHTML,
          which destroys all prior event listeners on the replaced children. ── */

    wirePanelControls(panel, sectionEl);
  }

  /**
   * Attach all interactive behaviors to elements INSIDE the filter panel body.
   * Safe to call repeatedly — event listeners are added fresh each call, and
   * innerHTML replacement destroys the old listeners, so no duplicates occur.
   * Does NOT include the panel open/close/focus-trap setup, which runs once
   * against elements that never get swapped (trigger button, close button,
   * backdrop, focus trap sentinels).
   */
  function wirePanelControls(panel, sectionEl) {

    /* ── Accordion groups ─────────────────────────── */

    var groups = panel.querySelectorAll('[data-gt-fg]');
    groups.forEach(function (group) {
      var header = group.querySelector('.gt-fg__header');
      var body = group.querySelector('.gt-fg__body');
      if (!header || !body) return;
      header.addEventListener('click', function () {
        var isExpanded = header.getAttribute('aria-expanded') === 'true';
        header.setAttribute('aria-expanded', String(!isExpanded));
        if (isExpanded) {
          group.classList.remove('gt-fg--open');
          body.hidden = true;
        } else {
          group.classList.add('gt-fg--open');
          body.hidden = false;
        }
      });
    });

    /* ── Show More overflow ───────────────────────── */

    panel.querySelectorAll('[data-gt-fg-show-more]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var body = btn.closest('.gt-fg__body');
        if (!body) return;
        var hidden = body.querySelectorAll('.gt-fg__option--overflow[hidden]');
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        if (expanded) {
          // Collapse back
          body.querySelectorAll('.gt-fg__option--overflow').forEach(function (opt) { opt.hidden = true; });
          btn.setAttribute('aria-expanded', 'false');
          var collapsedCount = body.querySelectorAll('.gt-fg__option--overflow').length;
          btn.querySelector('.gt-fg__show-more-label').textContent = t(sectionEl, 'filter_show_more', { count: collapsedCount });
        } else {
          hidden.forEach(function (opt) { opt.hidden = false; });
          btn.setAttribute('aria-expanded', 'true');
          btn.querySelector('.gt-fg__show-more-label').textContent = t(sectionEl, 'filter_show_less');
        }
      });
    });

    /* ── Instant-apply for list/boolean filters ───── */

    panel.querySelectorAll('[data-gt-filter-link]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        if (link.getAttribute('aria-disabled') === 'true') {
          e.preventDefault();
          return;
        }
        if (e.metaKey || e.ctrlKey || e.shiftKey) return; // allow open-in-new-tab
        e.preventDefault();
        var targetUrl = new URL(link.getAttribute('href'), window.location.origin);
        var wasActive = link.classList.contains('gt-fg__chip-remove') ||
                        link.querySelector('.gt-fg__checkbox--checked') !== null;
        var msgKey = wasActive ? 'a11y_filter_removed' : 'a11y_filter_applied';
        if (window.GTCollection && typeof window.GTCollection.fetchAndSwap === 'function') {
          window.GTCollection.fetchAndSwap(sectionEl, targetUrl, {
            live: t(sectionEl, msgKey, { count: '', total: '' })
          });
        } else {
          window.location.href = targetUrl.toString();
        }
      });
    });

    // Also wire above-grid filter chip removes (same pattern)
    sectionEl.querySelectorAll('.gt-col__chip-remove, .gt-col__chip-clear-all').forEach(function (link) {
      if (link.hasAttribute('data-gt-filter-link')) return; // already wired
      link.setAttribute('data-gt-filter-link', '');
      link.addEventListener('click', function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey) return;
        e.preventDefault();
        var targetUrl = new URL(link.getAttribute('href'), window.location.origin);
        if (window.GTCollection && typeof window.GTCollection.fetchAndSwap === 'function') {
          window.GTCollection.fetchAndSwap(sectionEl, targetUrl, {
            live: t(sectionEl, 'a11y_filter_removed', { count: '', total: '' })
          });
        } else {
          window.location.href = targetUrl.toString();
        }
      });
    });

    /* ── Price range slider ───────────────────────── */

    panel.querySelectorAll('[data-gt-fg-price]').forEach(function (priceEl) {
      initPriceSlider(priceEl, sectionEl);
    });
  }

  /**
   * Initialize a single price range control: dual slider + inputs + Apply button.
   * Uses pointer events for drag. Histogram buckets highlighted based on current range.
   */
  function initPriceSlider(priceEl, sectionEl) {
    var rangeMin = parseFloat(priceEl.getAttribute('data-range-min')) || 0;
    var rangeMax = parseFloat(priceEl.getAttribute('data-range-max')) || 100;
    var curMin   = parseFloat(priceEl.getAttribute('data-current-min')) || rangeMin;
    var curMax   = parseFloat(priceEl.getAttribute('data-current-max')) || rangeMax;
    var paramMin = priceEl.getAttribute('data-param-min') || 'filter.v.price.gte';
    var paramMax = priceEl.getAttribute('data-param-max') || 'filter.v.price.lte';

    if (rangeMax <= rangeMin) return; // Bad data — skip

    var sliderEl = priceEl.querySelector('[data-gt-fg-slider]');
    var fillEl   = priceEl.querySelector('[data-gt-fg-fill]');
    var handles  = priceEl.querySelectorAll('[data-gt-fg-handle]');
    var inputs   = priceEl.querySelectorAll('[data-gt-fg-price-input]');
    var applyBtn = priceEl.querySelector('[data-gt-fg-price-apply]');
    var histBars = priceEl.querySelectorAll('[data-bucket]');
    if (!sliderEl || !fillEl || handles.length !== 2) return;

    function pctFromValue(v) {
      return Math.max(0, Math.min(100, ((v - rangeMin) / (rangeMax - rangeMin)) * 100));
    }

    function valueFromPct(pct) {
      return rangeMin + (pct / 100) * (rangeMax - rangeMin);
    }

    function updateVisuals() {
      var minPct = pctFromValue(curMin);
      var maxPct = pctFromValue(curMax);
      handles[0].style.left = minPct + '%';
      handles[1].style.left = maxPct + '%';
      fillEl.style.left = minPct + '%';
      fillEl.style.right = (100 - maxPct) + '%';
      handles[0].setAttribute('aria-valuenow', Math.round(curMin));
      handles[1].setAttribute('aria-valuenow', Math.round(curMax));

      // Highlight histogram bars inside the range
      var bucketCount = histBars.length;
      histBars.forEach(function (bar, i) {
        var bucketStart = (i / bucketCount) * 100;
        var bucketEnd = ((i + 1) / bucketCount) * 100;
        var inRange = bucketEnd >= minPct && bucketStart <= maxPct;
        bar.classList.toggle('gt-fg__hist-bar--active', inRange);
        // Default bucket height — gives a visual mountain profile even without real data
        var heightPct = 30 + Math.abs(Math.sin((i / bucketCount) * Math.PI)) * 60;
        bar.style.height = heightPct + '%';
      });

      // Sync input values
      if (inputs[0]) inputs[0].value = Math.round(curMin);
      if (inputs[1]) inputs[1].value = Math.round(curMax);
    }

    updateVisuals();

    /* Drag logic — pointer events */
    var activeHandle = null;

    function onPointerDown(e) {
      e.preventDefault();
      activeHandle = this;
      activeHandle.setPointerCapture(e.pointerId);
      activeHandle.addEventListener('pointermove', onPointerMove);
      activeHandle.addEventListener('pointerup', onPointerUp);
      activeHandle.addEventListener('pointercancel', onPointerUp);
    }

    function onPointerMove(e) {
      if (!activeHandle) return;
      var rect = sliderEl.getBoundingClientRect();
      if (rect.width === 0) return;
      var pct = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
      var value = Math.round(valueFromPct(pct));
      var isMin = activeHandle.getAttribute('data-gt-fg-handle') === 'min';
      if (isMin) {
        curMin = Math.min(value, curMax - 1);
      } else {
        curMax = Math.max(value, curMin + 1);
      }
      updateVisuals();
    }

    function onPointerUp(e) {
      if (!activeHandle) return;
      activeHandle.releasePointerCapture(e.pointerId);
      activeHandle.removeEventListener('pointermove', onPointerMove);
      activeHandle.removeEventListener('pointerup', onPointerUp);
      activeHandle.removeEventListener('pointercancel', onPointerUp);
      activeHandle = null;
    }

    handles.forEach(function (h) {
      h.addEventListener('pointerdown', onPointerDown);
      // Keyboard: ArrowLeft/Right to nudge
      h.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        var step = Math.max(1, Math.round((rangeMax - rangeMin) / 100));
        var isMin = h.getAttribute('data-gt-fg-handle') === 'min';
        var delta = e.key === 'ArrowLeft' ? -step : step;
        if (isMin) {
          curMin = Math.max(rangeMin, Math.min(curMin + delta, curMax - 1));
        } else {
          curMax = Math.min(rangeMax, Math.max(curMax + delta, curMin + 1));
        }
        updateVisuals();
      });
    });

    /* Input field edits */
    inputs.forEach(function (input) {
      input.addEventListener('change', function () {
        var v = parseFloat(input.value);
        if (isNaN(v)) return;
        var isMin = input.getAttribute('data-gt-fg-price-input') === 'min';
        if (isMin) {
          curMin = Math.max(rangeMin, Math.min(v, curMax - 1));
        } else {
          curMax = Math.min(rangeMax, Math.max(v, curMin + 1));
        }
        updateVisuals();
      });
    });

    /* Apply button — build URL and swap */
    function applyPrice() {
      var url = new URL(window.location.href);
      // Price filter uses Shopify native params; value in cents
      var minCents = Math.round(curMin * 100);
      var maxCents = Math.round(curMax * 100);
      if (curMin > rangeMin) {
        url.searchParams.set(paramMin, String(minCents));
      } else {
        url.searchParams.delete(paramMin);
      }
      if (curMax < rangeMax) {
        url.searchParams.set(paramMax, String(maxCents));
      } else {
        url.searchParams.delete(paramMax);
      }
      url.searchParams.delete('page');
      if (window.GTCollection && typeof window.GTCollection.fetchAndSwap === 'function') {
        window.GTCollection.fetchAndSwap(sectionEl, url, {
          live: t(sectionEl, 'a11y_filter_applied', { count: '', total: '' })
        });
      } else {
        window.location.href = url.toString();
      }
    }

    if (applyBtn) applyBtn.addEventListener('click', applyPrice);
  }

  /* ═══════════════════════════════════════════════════════════
     MODULE 7 — QUICK ADD (Session 4)
     Two triggers on product cards:
       · [data-gt-add-to-cart] (+ icon) → direct /cart/add.js
       · [data-gt-quick-add]   (hover bar) → drawer IF multi-variant,
                                             else same as + icon
     POSTs to /cart/add.js, dispatches Heritage's cart:update event,
     then Heritage's cart drawer opens automatically.
  ═══════════════════════════════════════════════════════════ */

  /**
   * Cart operation queue. Multiple rapid clicks on different cards would fire
   * parallel POSTs, and if their responses arrive out of order, morphSection
   * calls would apply older cart snapshots on top of newer ones — user sees
   * wrong drawer contents. Queue serializes them.
   *
   * In-flight promise is stored; each new cartAdd chains onto it.
   * Errors don't break the queue — failures still reset the tail so the
   * next add can proceed.
   */
  var cartQueue = Promise.resolve();

  /**
   * POST to Shopify's /cart/add.js endpoint using the Heritage contract.
   *
   * Why this is the ONLY way that works with Heritage's cart drawer:
   *   1. We must request sections=<list-of-cart-section-ids> so Shopify
   *      returns pre-rendered HTML fragments for each cart section.
   *   2. We dispatch a CustomEvent whose detail exactly matches Heritage's
   *      CartAddEvent contract (see assets/events.js:91-116):
   *        detail: { resource, sourceId, data: { sections, source,
   *                                              productId, variantId,
   *                                              itemCount } }
   *   3. Heritage's component-cart-items.js listens for this event and
   *      reads detail.data.sections[sectionId] to morph its cart HTML.
   *      Without sections, it falls back to fetching the whole page —
   *      which is what was happening and causing the blank-drawer bug.
   *   4. Heritage's cart-drawer-component auto-opens IF the merchant has
   *      enabled `settings.auto_open_cart_drawer` in Theme Editor.
   *
   * Returns the parsed response (augmented with .sections) on success.
   */
  function cartAdd(variantId, quantity, meta) {
    meta = meta || {};

    // Chain onto the queue tail. Catch ensures one failure doesn't break
    // the chain — subsequent adds still process.
    var next = cartQueue.then(
      function () { return cartAddImpl(variantId, quantity, meta); },
      function () { return cartAddImpl(variantId, quantity, meta); }
    );
    cartQueue = next.catch(function () { /* swallow so chain stays alive */ });
    return next;
  }

  function cartAddImpl(variantId, quantity, meta) {
    // 1. Collect all cart-items-component section IDs on the page.
    //    These are the sections Shopify must re-render for us.
    var cartComponents = document.querySelectorAll('cart-items-component[data-section-id]');
    var sectionIds = [];
    cartComponents.forEach(function (comp) {
      var sid = comp.getAttribute('data-section-id');
      if (sid && sectionIds.indexOf(sid) === -1) sectionIds.push(sid);
    });

    // 2. Build FormData body (Heritage uses 'application/javascript' accept
    //    with FormData, not JSON — this is what Shopify expects for section
    //    rendering via /cart/add.js).
    var formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', String(Number(quantity) || 1));
    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var addUrl = routeRoot.replace(/\/$/, '') + '/cart/add.js';

    return fetch(addUrl, {
      method: 'POST',
      headers: {
        'Accept': 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData,
      credentials: 'same-origin'
    }).then(function (res) {
      return res.text().then(function (text) {
        var data;
        try { data = JSON.parse(text); }
        catch (e) {
          throw new Error('Invalid cart response: ' + text.slice(0, 80));
        }
        if (!res.ok || data.status) {
          var err = new Error(data.description || data.message || 'Cart add failed');
          err.response = data;
          throw err;
        }
        return data;
      });
    }).then(function (data) {
      // 3. Dispatch Heritage-contract-compliant CartAddEvent.
      //    CartAddEvent.eventName === 'cart:update' (same string).
      //    Heritage's component-cart-items handleCartUpdate reads
      //    detail.data.sections[this.sectionId] to morph the cart HTML.
      var evt = new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: data,
          sourceId: meta.sourceId || 'gt-collection',
          data: {
            source: 'gt-collection',
            sections: data.sections || {},
            productId: meta.productId || '',
            variantId: String(variantId),
            itemCount: Number(quantity) || 1
          }
        }
      });
      document.dispatchEvent(evt);
      return data;
    });
  }

  /**
   * Open Heritage's cart drawer immediately — used for optimistic UI so
   * the drawer slide-in animation overlaps with the /cart/add.js POST,
   * making the experience feel instant. Safe to call even if the drawer
   * is already open or missing (no-op in both cases).
   *
   * Returns the drawer element for callers that need to close it on error.
   */
  function openCartDrawerOptimistic() {
    var drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return null;
    try {
      // Prefer the public open() method; fall back to showDialog()
      if (typeof drawer.open === 'function') drawer.open();
      else if (typeof drawer.showDialog === 'function') drawer.showDialog();
    } catch (e) {
      if (window.console && console.error) console.error('[gt-collection] cart drawer open failed:', e);
      return null;
    }
    return drawer;
  }

  /**
   * Close Heritage's cart drawer — used when an optimistic open was made
   * but the POST failed.
   */
  function closeCartDrawer(drawer) {
    if (!drawer) return;
    try {
      if (typeof drawer.close === 'function') drawer.close();
      else if (typeof drawer.closeDialog === 'function') drawer.closeDialog();
    } catch (e) { /* swallow */ }
  }

  /**
   * Initialize the direct "+" add-to-cart buttons on all product cards.
   * Also handles the Quick Add hover bar — if the card has multiple variants,
   * opens the drawer; otherwise, same direct-add behavior as the + icon.
   */
  function initCardAddButtons(sectionEl) {
    sectionEl.addEventListener('click', function (e) {
      // Direct + icon add
      var addBtn = e.target.closest('[data-gt-add-to-cart]');
      if (addBtn && sectionEl.contains(addBtn)) {
        e.preventDefault();
        e.stopPropagation();
        handleDirectAdd(addBtn, sectionEl);
        return;
      }
      // Quick Add hover bar
      var qaBtn = e.target.closest('[data-gt-quick-add]');
      if (qaBtn && sectionEl.contains(qaBtn)) {
        e.preventDefault();
        e.stopPropagation();
        var card = qaBtn.closest('.gt-card');
        if (!card) return;
        var hasMulti = card.getAttribute('data-has-multiple-variants') === 'true';
        if (hasMulti) {
          openQuickAddDrawer(card, sectionEl);
        } else {
          // Single-variant → direct add (same as + icon)
          handleDirectAdd(qaBtn, sectionEl, card.getAttribute('data-default-variant-id'));
        }
      }
    });
  }

  /**
   * Direct-add flow: spinner → POST → success tick → event → cart drawer opens.
   */
  function handleDirectAdd(button, sectionEl, forcedVariantId) {
    if (button.getAttribute('aria-busy') === 'true') return; // re-entry guard
    var card = button.closest('.gt-card');
    var variantId = forcedVariantId || button.getAttribute('data-variant-id');
    if (!variantId) {
      // Hover bar trigger on single-variant card — pull from parent article
      variantId = card && card.getAttribute('data-default-variant-id');
    }
    if (!variantId) return;

    button.setAttribute('aria-busy', 'true');
    button.classList.add('gt-card__atc-icon--loading');

    // Optimistic: open the cart drawer immediately so its slide-in animation
    // overlaps the POST. User sees instant response. Drawer shows existing
    // cart; Heritage's morphSection will splice in the new line item when
    // the POST resolves. If the POST fails, we close the drawer + show the
    // error banner on the button as usual.
    var openedDrawer = openCartDrawerOptimistic();

    cartAdd(variantId, 1, {
      productId: card && card.getAttribute('data-product-id'),
      sourceId: card && card.getAttribute('data-product-id')
    })
      .then(function () {
        button.classList.remove('gt-card__atc-icon--loading');
        button.classList.add('gt-card__atc-icon--success');
        liveAnnounce(sectionEl, t(sectionEl, 'a11y_cart_added'));
        // Hold success state briefly, then reset
        setTimeout(function () {
          button.classList.remove('gt-card__atc-icon--success');
          button.setAttribute('aria-busy', 'false');
        }, 1500);
      })
      .catch(function (err) {
        button.classList.remove('gt-card__atc-icon--loading');
        button.setAttribute('aria-busy', 'false');
        // Roll back optimistic open — user should not be left looking at a
        // drawer that doesn't reflect their failed action.
        closeCartDrawer(openedDrawer);
        liveAnnounce(sectionEl, t(sectionEl, 'a11y_cart_error'));
        if (window.console && console.error) console.error('[gt-collection] Direct add failed:', err);
      });
  }

  /**
   * Initialize the Quick Add drawer once per section.
   */
  function initQuickAddDrawer(sectionEl) {
    // Drawer is outside the section wrapper structurally inside our section
    // template — same document, but we look up by matching section id.
    var drawer = sectionEl.querySelector('[data-gt-qa]') ||
                 document.querySelector('[data-gt-qa]');
    if (!drawer) return;

    var backdrop = sectionEl.querySelector('[data-gt-qa-backdrop]') ||
                   document.querySelector('[data-gt-qa-backdrop]');
    var closeBtn = drawer.querySelector('[data-gt-qa-close]');
    var trapStart = drawer.querySelector('[data-gt-qa-trap="start"]');
    var trapEnd = drawer.querySelector('[data-gt-qa-trap="end"]');

    var lastFocused = null;
    var activeVariantId = null;
    var activeCard = null;

    /* ── Open / close ────────────────────────────── */

    function open() {
      lastFocused = document.activeElement;
      drawer.hidden = false;
      if (backdrop) { backdrop.hidden = false; backdrop.setAttribute('aria-hidden', 'false'); }
      document.body.classList.add('gt-body--qa-open');
      requestAnimationFrame(function () {
        drawer.classList.add('gt-qa--open');
        if (backdrop) backdrop.classList.add('gt-qa-backdrop--open');
        if (closeBtn) closeBtn.focus();
      });
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      drawer.classList.remove('gt-qa--open');
      if (backdrop) backdrop.classList.remove('gt-qa-backdrop--open');
      document.body.classList.remove('gt-body--qa-open');
      document.removeEventListener('keydown', onKeydown);
      setTimeout(function () {
        drawer.hidden = true;
        if (backdrop) { backdrop.hidden = true; backdrop.setAttribute('aria-hidden', 'true'); }
      }, 300);
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
      activeCard = null;
      activeVariantId = null;
    }

    function onKeydown(e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); }
    }

    if (closeBtn) closeBtn.addEventListener('click', close);
    if (backdrop) backdrop.addEventListener('click', close);

    /* ── Focus trap ──────────────────────────────── */
    function focusables() {
      var all = Array.prototype.slice.call(drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'));
      return all.filter(function (el) {
        return !el.hasAttribute('data-gt-qa-trap') && el.offsetParent !== null;
      });
    }
    if (trapStart) trapStart.addEventListener('focus', function () {
      var els = focusables(); if (els.length) els[els.length - 1].focus();
    });
    if (trapEnd) trapEnd.addEventListener('focus', function () {
      var els = focusables(); if (els.length) els[0].focus();
    });

    /* ── Qty buttons ─────────────────────────────── */
    var qtyInput = drawer.querySelector('[data-gt-qa-qty-input]');
    var qtyDec = drawer.querySelector('[data-gt-qa-qty-dec]');
    var qtyInc = drawer.querySelector('[data-gt-qa-qty-inc]');

    if (qtyDec) qtyDec.addEventListener('click', function () {
      var v = Math.max(1, parseInt(qtyInput.value, 10) - 1 || 1);
      qtyInput.value = v; updateAtcLabel();
    });
    if (qtyInc) qtyInc.addEventListener('click', function () {
      var v = Math.max(1, parseInt(qtyInput.value, 10) + 1 || 2);
      qtyInput.value = v; updateAtcLabel();
    });
    if (qtyInput) qtyInput.addEventListener('change', function () {
      var v = Math.max(1, parseInt(qtyInput.value, 10) || 1);
      qtyInput.value = v; updateAtcLabel();
    });

    /* ── Variant tile picker ─────────────────────── */
    function renderVariants(variants, product) {
      var wrap = drawer.querySelector('[data-gt-qa-variants]');
      var wrapParent = drawer.querySelector('[data-gt-qa-variants-wrap]');
      if (!wrap) return;
      wrap.innerHTML = '';

      if (!variants || variants.length <= 1) {
        // Single variant → hide the wrap entirely
        if (wrapParent) wrapParent.hidden = true;
        if (variants && variants.length === 1) {
          activeVariantId = variants[0].id;
        }
        return;
      }
      if (wrapParent) wrapParent.hidden = false;

      // Find index of first available variant to use as default selection.
      // If we just used index 0 and that variant is sold out, the drawer
      // would open with a disabled variant pre-selected — users clicking
      // Add to Cart would hit "out of stock" errors.
      var defaultIdx = 0;
      for (var vi = 0; vi < variants.length; vi++) {
        if (variants[vi].available) { defaultIdx = vi; break; }
      }

      variants.forEach(function (v, i) {
        var tile = document.createElement('button');
        tile.type = 'button';
        tile.className = 'gt-vt' + (v.available ? '' : ' gt-vt--disabled');
        tile.setAttribute('role', 'radio');
        tile.setAttribute('data-variant-id', String(v.id));
        tile.setAttribute('aria-checked', i === defaultIdx ? 'true' : 'false');
        if (i === defaultIdx) { tile.classList.add('gt-vt--active'); activeVariantId = v.id; }
        if (!v.available) { tile.disabled = true; }

        var name = document.createElement('span');
        name.className = 'gt-vt__name';
        name.textContent = v.title || v.option1 || 'Variant';
        tile.appendChild(name);

        if (v.price != null) {
          var price = document.createElement('span');
          price.className = 'gt-vt__price';
          price.textContent = formatMoney(v.price);
          tile.appendChild(price);
        }

        tile.addEventListener('click', function () {
          if (!v.available) return;
          wrap.querySelectorAll('.gt-vt').forEach(function (t) {
            t.classList.remove('gt-vt--active');
            t.setAttribute('aria-checked', 'false');
          });
          tile.classList.add('gt-vt--active');
          tile.setAttribute('aria-checked', 'true');
          activeVariantId = v.id;
          updatePrice(v);
          updateAtcLabel();
        });

        wrap.appendChild(tile);
      });
    }

    /**
     * Shopify returns price in cents (e.g. 26900 = Rs. 269.00).
     * We read from the embedded JSON in product cards, so keep that source of truth.
     */
    function formatMoney(cents) {
      var n = Number(cents);
      if (isNaN(n)) return '';
      // Use Shopify's money format if available; else fall back to rupees
      var formatted = (n / 100).toFixed(2);
      // Comma formatting for thousands (Indian locale: X,XX,XXX)
      var parts = formatted.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return 'Rs. ' + parts.join('.');
    }

    function updatePrice(variant) {
      var priceEl = drawer.querySelector('[data-gt-qa-price]');
      var compareEl = drawer.querySelector('[data-gt-qa-price-compare]');
      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
    }

    function updateAtcLabel() {
      var label = drawer.querySelector('[data-gt-qa-atc-label]');
      if (!label) return;
      var qty = parseInt(qtyInput && qtyInput.value, 10) || 1;
      // Find the active variant's price
      var activeTile = drawer.querySelector('.gt-vt--active');
      var priceCents = 0;
      var priceTextEl = drawer.querySelector('[data-gt-qa-price]');
      if (activeTile) {
        // Re-parse active tile's price
        var priceSpan = activeTile.querySelector('.gt-vt__price');
        if (priceSpan) {
          var priceStr = priceSpan.textContent.replace(/[^0-9.]/g, '');
          priceCents = Math.round(parseFloat(priceStr) * 100);
        }
      }
      if (!priceCents && priceTextEl) {
        var pStr = priceTextEl.textContent.replace(/[^0-9.]/g, '');
        priceCents = Math.round(parseFloat(pStr) * 100);
      }
      var total = priceCents * qty;
      label.textContent = t(sectionEl, 'quick_add_atc', { price: formatMoney(total) });
    }

    /* ── ATC button ──────────────────────────────── */
    var atcBtn = drawer.querySelector('[data-gt-qa-atc]');
    if (atcBtn) atcBtn.addEventListener('click', function () {
      if (atcBtn.getAttribute('aria-busy') === 'true') return;
      if (!activeVariantId) return;
      var qty = parseInt(qtyInput && qtyInput.value, 10) || 1;
      var errorEl = drawer.querySelector('[data-gt-qa-error]');
      if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }

      atcBtn.setAttribute('aria-busy', 'true');
      atcBtn.classList.add('gt-qa__atc--loading');

      // Optimistic: close this drawer immediately and open the cart drawer
      // so the transition feels fluid. On error, close cart drawer + show
      // error via live region (we lose the inline error banner in exchange
      // for snappy UX — acceptable trade-off, 99% of adds succeed).
      close();
      var openedDrawer = openCartDrawerOptimistic();

      cartAdd(activeVariantId, qty, {
        productId: activeCard && activeCard.getAttribute('data-product-id'),
        sourceId: activeCard && activeCard.getAttribute('data-product-id')
      })
        .then(function () {
          atcBtn.setAttribute('aria-busy', 'false');
          atcBtn.classList.remove('gt-qa__atc--loading');
          liveAnnounce(sectionEl, t(sectionEl, 'a11y_cart_added'));
        })
        .catch(function (err) {
          atcBtn.setAttribute('aria-busy', 'false');
          atcBtn.classList.remove('gt-qa__atc--loading');
          closeCartDrawer(openedDrawer);
          liveAnnounce(sectionEl, (err && err.message) || t(sectionEl, 'a11y_cart_error'));
          if (window.console && console.error) console.error('[gt-collection] Quick Add failed:', err);
        });
    });

    /* ── Drawer open API ─────────────────────────── */
    function openQuickAddDrawer(card, sec) {
      activeCard = card;

      // Read embedded variants JSON from card
      var variantScript = card.querySelector('script[data-gt-variants]');
      var variants = [];
      try {
        if (variantScript) variants = JSON.parse(variantScript.textContent);
      } catch (e) {
        if (window.console && console.error) console.error('[gt-collection] Variant JSON parse failed:', e);
      }

      // Populate drawer
      drawer.querySelector('[data-gt-qa-name]').textContent = card.getAttribute('data-product-title') || '';

      var firstImg = card.querySelector('.gt-card__img');
      var imgTarget = drawer.querySelector('[data-gt-qa-img]');
      if (imgTarget) {
        imgTarget.innerHTML = '';
        if (firstImg && firstImg.tagName === 'IMG') {
          var cloned = firstImg.cloneNode(true);
          cloned.removeAttribute('class');
          cloned.removeAttribute('loading');
          cloned.removeAttribute('fetchpriority');
          cloned.style.width = '100%';
          cloned.style.height = '100%';
          cloned.style.objectFit = 'cover';
          imgTarget.appendChild(cloned);
        }
      }

      // Price display — use first AVAILABLE variant so we don't flash the
      // drawer showing a sold-out variant's price.
      var firstAvailable = null;
      for (var pvi = 0; pvi < variants.length; pvi++) {
        if (variants[pvi].available) { firstAvailable = variants[pvi]; break; }
      }
      if (firstAvailable) updatePrice(firstAvailable);
      else if (variants[0]) updatePrice(variants[0]);

      // Variant tiles
      renderVariants(variants, null);

      // Qty reset
      if (qtyInput) qtyInput.value = '1';

      // View full details link
      var viewFullLink = drawer.querySelector('[data-gt-qa-view-full]');
      if (viewFullLink) viewFullLink.href = card.getAttribute('data-product-url') || '#';

      // ATC label update
      updateAtcLabel();

      open();
    }

    // Expose opener so initCardAddButtons can call it
    sectionEl._gtOpenQuickAdd = openQuickAddDrawer;
  }

  /**
   * Wrapper that calls the section-scoped openQuickAddDrawer attached
   * by initQuickAddDrawer. Separated so initCardAddButtons (which runs
   * on click, not init) can reach it reliably.
   */
  function openQuickAddDrawer(card, sectionEl) {
    if (typeof sectionEl._gtOpenQuickAdd === 'function') {
      sectionEl._gtOpenQuickAdd(card, sectionEl);
    }
  }

  /* ═══════════════════════════════════════════════════════════
     INIT — bootstrap all modules on every gt-collection section
  ═══════════════════════════════════════════════════════════ */

  function initAll() {
    var sections = document.querySelectorAll('[data-gt-col]');
    sections.forEach(function (sectionEl) {
      if (sectionEl.hasAttribute('data-gt-col-initialized')) return;
      sectionEl.setAttribute('data-gt-col-initialized', 'true');

      initSortDropdown(sectionEl);
      initLoadMore(sectionEl);
      initTouchSuppression(sectionEl);
      initStickyToolbar(sectionEl);
      initFilterPanel(sectionEl);
      initQuickAddDrawer(sectionEl);
      initCardAddButtons(sectionEl);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init after Shopify Theme Editor section reload (common pattern)
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector('[data-gt-col]')) initAll();
  });

  // Handle browser back/forward across filter/sort states
  window.addEventListener('popstate', function () {
    // Simplest path: reload on popstate so server-rendered state matches URL
    // (avoids complex client-side state reconciliation)
    window.location.reload();
  });

})();
